import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import Header from "./Header";
import ManualAnalysis from "./ManualAnalysis";
import FeedCardList from "./FeedCardList";
import InstrumentStrip from "./InstrumentStrip";
import FilterBar from "./FilterBar";
import InstrumentTags from "./InstrumentTags";
import ConnectionStatus from "./ConnectionStatus";
import WatchedAccounts from "./WatchedAccounts";

import AlertSettings from "./AlertSettings";
import ScoreKey from "./ScoreKey";
import SentimentTimeline from "./SentimentTimeline";
import RecapPanel from "./RecapPanel";
import useSentinelWebSocket from "@/hooks/useSentinelWebSocket";
import { Menu, X } from "lucide-react";
import { playAlertSound, getSoundEnabled } from "@/lib/sound";
import { logger } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1/sentinel`;

export default function MacroFlare() {
  const [posts, setPosts] = useState([]);
  const [aggregation, setAggregation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [newPostCount, setNewPostCount] = useState(0);
  const [timelineRefresh, setTimelineRefresh] = useState(0);
  const [systemStatus, setSystemStatus] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);
  const [recapReady, setRecapReady] = useState(false);
  const [pipelineHealth, setPipelineHealth] = useState(null);
  const scrollRef = useRef(null);
  const isScrolledUp = useRef(false);

  const { connected, on } = useSentinelWebSocket();

  // Check if today's recap is ready (green dot indicator)
  useEffect(() => {
    const checkRecap = async () => {
      try {
        const res = await axios.get(`${API}/recap/latest`);
        if (res.data.status !== "none") {
          const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
          setRecapReady(res.data.date === today);
        }
      } catch (err) {
        logger.debug("[MacroFlare] Recap check:", err.message);
      }
    };
    checkRecap();
    const interval = setInterval(checkRecap, 5 * 60 * 1000); // Check every 5 min
    return () => clearInterval(interval);
  }, []);

  // Pipeline health for header indicator (every 30s)
  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/pipeline/status`);
        setPipelineHealth(res.data);
      } catch (err) {
        logger.debug("[MacroFlare] Pipeline health:", err.message);
      }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch initial feed
  const fetchFeed = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feed?limit=50`);
      setPosts(res.data.posts || []);
      setHasMore(res.data.has_more || false);
    } catch (err) {
      console.error("Failed to fetch feed:", err);
    }
  }, []);

  // Fetch aggregation
  const fetchAggregation = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/aggregation`);
      setAggregation(res.data);
    } catch (err) {
      console.error("Failed to fetch aggregation:", err);
    }
  }, []);

  // Fetch system status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/status`);
      setSystemStatus(res.data);
    } catch (err) {
      console.error("Failed to fetch status:", err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchFeed(), fetchAggregation(), fetchStatus()]);
      setInitialLoading(false);
    };
    init();
  }, [fetchFeed, fetchAggregation, fetchStatus]);

  // Periodic feed sync — catches any posts missed during WebSocket disconnections
  useEffect(() => {
    const interval = setInterval(() => {
      fetchFeed();
      fetchAggregation();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchFeed, fetchAggregation]);

  // WebSocket: new_post handler
  useEffect(() => {
    const unsub = on("new_post", (data) => {
      setPosts((prev) => {
        if (prev.some((p) => p.post_id === data.post_id)) return prev;
        const updated = [data, ...prev];
        // Re-sort by posted_at descending so batch arrivals stay chronological
        updated.sort((a, b) => {
          const ta = a.timestamps?.posted_at || a.timestamps?.analyzed_at || "";
          const tb = b.timestamps?.posted_at || b.timestamps?.analyzed_at || "";
          return tb.localeCompare(ta);
        });
        return updated;
      });
      if (isScrolledUp.current) {
        setNewPostCount((c) => c + 1);
      }
      setTimelineRefresh((c) => c + 1);
      // Sound alert for extreme scores
      const score = data.analysis?.overall_score;
      if (score != null && Math.abs(score) >= 4 && getSoundEnabled()) {
        playAlertSound(score < 0 ? 'bear' : 'bull');
      }
    });
    return unsub;
  }, [on]);

  // WebSocket: aggregation_update handler
  useEffect(() => {
    const unsub = on("aggregation_update", (data) => {
      setAggregation(data);
    });
    return unsub;
  }, [on]);

  // On reconnect, refetch
  useEffect(() => {
    if (connected) {
      fetchFeed();
      fetchAggregation();
    }
  }, [connected, fetchFeed, fetchAggregation]);

  // Handle manual analysis
  const handleAnalyze = async (text, sourceHandle) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/analyze`, {
        text,
        source_handle: sourceHandle,
      });
      setPosts((prev) => {
        if (prev.some((p) => p.post_id === res.data.post_id)) return prev;
        return [res.data, ...prev];
      });
      fetchAggregation();
      setSidebarOpen(false); // Close sidebar on mobile after analyze
      return res.data;
    } catch (err) {
      const message = err.response?.data?.detail || "Analysis failed. Please try again.";
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle clear feed
  const handleClearFeed = async () => {
    try {
      await axios.post(`${API}/feed/clear`);
      setPosts([]);
      setAggregation(null);
      setNewPostCount(0);
      setHasMore(false);
      fetchAggregation();
    } catch (err) {
      console.error("Failed to clear feed:", err);
    }
  };

  // Handle load more (cursor-based pagination)
  const handleLoadMore = async () => {
    if (loadingMore || posts.length === 0) return;
    setLoadingMore(true);
    try {
      const oldestPost = posts[posts.length - 1];
      const beforeTimestamp = oldestPost?.timestamps?.posted_at;
      if (!beforeTimestamp) return;

      const res = await axios.get(`${API}/feed?limit=50&before=${encodeURIComponent(beforeTimestamp)}`);
      const olderPosts = res.data.posts || [];
      setHasMore(res.data.has_more || false);

      if (olderPosts.length > 0) {
        setPosts((prev) => {
          const existingIds = new Set(prev.map(p => p.post_id));
          const newPosts = olderPosts.filter(p => !existingIds.has(p.post_id));
          return [...prev, ...newPosts];
        });
      }
    } catch (err) {
      console.error("Failed to load more:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Scroll tracking
  const handleScroll = useCallback((e) => {
    const el = e.target;
    isScrolledUp.current = el.scrollTop > 50;
    if (!isScrolledUp.current) {
      setNewPostCount(0);
    }
  }, []);

  const scrollToTop = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      setNewPostCount(0);
    }
  }, []);

  // Filter state
  const [filters, setFilters] = useState({
    instruments: [],
    scorePreset: null,
    sources: [],
    tags: [],
  });

  const toggleFilter = useCallback((type, value) => {
    setFilters(prev => {
      const arr = prev[type];
      return arr.includes(value)
        ? { ...prev, [type]: arr.filter(v => v !== value) }
        : { ...prev, [type]: [...arr, value] };
    });
  }, []);

  const setScorePreset = useCallback((preset) => {
    setFilters(prev => ({ ...prev, scorePreset: preset }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ instruments: [], scorePreset: null, sources: [], tags: [] });
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.instruments.length > 0) count++;
    if (filters.scorePreset) count++;
    if (filters.sources.length > 0) count++;
    if (filters.tags.length > 0) count++;
    return count;
  }, [filters]);

  const availableInstruments = useMemo(() =>
    [...new Set(posts.flatMap(p => (p.analysis?.instruments || []).map(i => i.symbol)))].sort(),
    [posts]
  );

  const availableSources = useMemo(() =>
    [...new Set(posts.map(p => p.source?.handle).filter(Boolean))].sort(),
    [posts]
  );

  const availableTags = useMemo(() =>
    [...new Set(posts.flatMap(p => p.analysis?.tags || []))].sort(),
    [posts]
  );

  const filteredPosts = useMemo(() => {
    if (activeFilterCount === 0) return posts;
    return posts.filter(post => {
      if (filters.instruments.length > 0) {
        const pi = (post.analysis?.instruments || []).map(i => i.symbol);
        if (!filters.instruments.some(f => pi.includes(f))) return false;
      }
      if (filters.scorePreset) {
        const s = post.analysis?.overall_score;
        if (s == null) return false;
        if (filters.scorePreset === 'bearish' && s >= 0) return false;
        if (filters.scorePreset === 'bullish' && s <= 0) return false;
        if (filters.scorePreset === 'strong' && Math.abs(s) < 3) return false;
        if (filters.scorePreset === 'extreme' && Math.abs(s) < 4) return false;
      }
      if (filters.sources.length > 0) {
        if (!filters.sources.includes(post.source?.handle)) return false;
      }
      if (filters.tags.length > 0) {
        const pt = post.analysis?.tags || [];
        if (!filters.tags.some(f => pt.includes(f))) return false;
      }
      return true;
    });
  }, [posts, filters, activeFilterCount]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0d1117' }}>
      <Header
        aggregation={aggregation}
        postCount={posts.length}
        connected={connected}
        mode={systemStatus?.mode}
        onRecapOpen={() => setRecapOpen(true)}
        recapReady={recapReady}
        pipelineHealth={pipelineHealth}
      />

      <RecapPanel isOpen={recapOpen} onClose={() => setRecapOpen(false)} />

      {/* Mobile sidebar toggle — fixed button */}
      <button
        data-testid="sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed bottom-4 left-4 z-50 flex items-center justify-center rounded-full shadow-lg"
        style={{
          width: 48,
          height: 48,
          backgroundColor: '#22c55e',
          color: '#0d1117',
        }}
      >
        {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      <div className="flex" style={{ height: 'calc(100vh - 56px)' }}>
        {/* Sidebar — hidden on mobile unless toggled */}
        {/* Overlay backdrop on mobile */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 z-30"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <aside
          className={`
            fixed lg:static inset-y-0 left-0 z-40
            transform transition-transform duration-200 ease-in-out
            lg:transform-none lg:translate-x-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            flex-shrink-0 overflow-y-auto border-r flex flex-col
          `}
          style={{
            width: '320px',
            maxWidth: '85vw',
            top: '56px',
            backgroundColor: '#0d1117',
            borderColor: '#30363d',
          }}
        >
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            <ConnectionStatus
              connected={connected}
              mode={systemStatus?.mode}
            />
            <ManualAnalysis
              onAnalyze={handleAnalyze}
              loading={loading}
              error={error}
            />
            <WatchedAccounts />
            <AlertSettings />
            <InstrumentTags aggregation={aggregation} />
            <ScoreKey />
          </div>
          <div className="mt-auto px-3 sm:px-4 pb-3 pt-2" style={{ borderTop: '1px solid #1c2333' }}>
            <p
              className="font-mono text-center"
              style={{ color: '#8b949e', fontSize: 11, lineHeight: 1.5 }}
              data-testid="sidebar-disclaimer"
            >
              MacroFlare provides real-time news analysis for informational purposes only. Not financial advice. Not a recommendation to buy, sell, or hold any security or commodity.
            </p>
          </div>
        </aside>

        {/* Main Feed */}
        <main
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-2 sm:p-4"
          onScroll={handleScroll}
        >
          <InstrumentStrip
            aggregation={aggregation}
            onToggleFilter={toggleFilter}
            activeFilters={filters.instruments}
          />
          <SentimentTimeline refreshKey={timelineRefresh} />

          <FilterBar
            filters={filters}
            onToggleFilter={toggleFilter}
            onSetScorePreset={setScorePreset}
            onClearAll={clearFilters}
            availableInstruments={availableInstruments}
            availableSources={availableSources}
            availableTags={availableTags}
            activeFilterCount={activeFilterCount}
          />

          {newPostCount > 0 && (
            <button
              data-testid="new-posts-badge"
              onClick={scrollToTop}
              className="sticky top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-colors duration-150"
              style={{
                backgroundColor: '#0a3320',
                color: '#22c55e',
                border: '1px solid #166534',
              }}
            >
              {'\u2191'} {newPostCount} new post{newPostCount !== 1 ? 's' : ''}
            </button>
          )}

          <FeedCardList
            posts={filteredPosts}
            loading={initialLoading}
            onClearFeed={handleClearFeed}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            totalCount={posts.length}
            activeFilterCount={activeFilterCount}
            onClearFilters={clearFilters}
            filters={filters}
            onToggleFilter={toggleFilter}
            aggregation={aggregation}
          />
        </main>
      </div>
    </div>
  );
}
